import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SearchFilterBar from '../../components/ui/SearchFilterBar';
import ClientStatsPanel from './components/ClientStatsPanel';
import ClientTable from './components/ClientTable';
import AddClientModal from './components/AddClientModal';
import ClientProfileModal from './components/ClientProfileModal';
import ExportModal from './components/ExportModal';
import Button from '../../components/ui/Button';
import { clientService } from '../../services/clientService';
import { useAuth } from '../../contexts/AuthContext';

const ClientDatabase = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  // Filter clients when search term or filters change
  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, categoryFilter, sortBy, sortOrder]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const { clients: clientsData, error } = await clientService?.getClients();
      
      if (error) {
        setError(error);
        return;
      }
      
      setClients(clientsData || []);
      setError(null);
    } catch (error) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    let filtered = [...clients];

    // Apply search filter
    if (searchTerm?.trim()) {
      filtered = filtered?.filter(client => {
        const fullName = `${client?.first_name} ${client?.last_name}`?.toLowerCase();
        const email = client?.email?.toLowerCase() || '';
        const term = searchTerm?.toLowerCase();
        
        return fullName?.includes(term) || email?.includes(term);
      });
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered?.filter(client => client?.category === categoryFilter);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a?.first_name} ${a?.last_name}`?.toLowerCase();
          bValue = `${b?.first_name} ${b?.last_name}`?.toLowerCase();
          break;
        case 'email':
          aValue = a?.email?.toLowerCase() || '';
          bValue = b?.email?.toLowerCase() || '';
          break;
        case 'category':
          aValue = a?.category || '';
          bValue = b?.category || '';
          break;
        case 'created_at':
        default:
          aValue = new Date(a?.created_at || 0);
          bValue = new Date(b?.created_at || 0);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredClients(filtered);
  };

  const handleAddClient = async (clientData) => {
    try {
      const { client, error } = await clientService?.createClient({
        ...clientData,
        created_by: user?.id
      });
      
      if (error) {
        throw new Error(error);
      }
      
      await loadClients(); // Reload to get updated data
      setIsAddModalOpen(false);
    } catch (error) {
      throw error; // Let modal handle the error display
    }
  };

  const handleEditClient = async (clientId, updates) => {
    try {
      const { client, error } = await clientService?.updateClient(clientId, updates);
      
      if (error) {
        throw new Error(error);
      }
      
      await loadClients(); // Reload to get updated data
      setIsProfileModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      throw error; // Let modal handle the error display
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      const { error } = await clientService?.deleteClient(clientId);
      
      if (error) {
        throw new Error(error);
      }
      
      await loadClients(); // Reload to get updated data
      setIsProfileModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      alert(`Failed to delete client: ${error?.message}`);
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setIsProfileModalOpen(true);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filters) => {
    setCategoryFilter(filters?.category || 'all');
    setSortBy(filters?.sortBy || 'created_at');
    setSortOrder(filters?.sortOrder || 'desc');
  };

  // Calculate stats
  const stats = {
    total: clients?.length || 0,
    individual: clients?.filter(c => c?.category === 'individual')?.length || 0,
    professional: clients?.filter(c => c?.category === 'professional')?.length || 0,
    subcontractor: clients?.filter(c => c?.category === 'subcontractor')?.length || 0,
    thisMonth: clients?.filter(c => {
      const createdDate = new Date(c?.created_at);
      const now = new Date();
      return createdDate?.getMonth() === now?.getMonth() && createdDate?.getFullYear() === now?.getFullYear();
    })?.length || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
                <p className="mt-4 text-text-secondary">Chargement des clients...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={loadClients} variant="primary">
                  Réessayer
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="px-4 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs />
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Base de Données Client
                </h1>
                <p className="text-text-secondary mt-1">
                  Gérez et organisez vos informations client
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => setIsExportModalOpen(true)}
                  className="hidden sm:flex"
                >
                  Exporter
                </Button>
                
                <Button
                  variant="ghost"
                  iconName="Download"
                  onClick={() => setIsExportModalOpen(true)}
                  className="sm:hidden"
                />
                
                <Button
                  variant="primary"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setIsAddModalOpen(true)}
                  className="hidden sm:flex"
                >
                  Nouveau Client
                </Button>
                
                <Button
                  variant="primary"
                  iconName="Plus"
                  onClick={() => setIsAddModalOpen(true)}
                  className="sm:hidden"
                />
              </div>
            </div>

            <ClientStatsPanel stats={stats} />
            
            <div className="bg-card rounded-lg border border-border shadow-sm">
              <div className="p-6 border-b border-border">
                <SearchFilterBar
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  searchPlaceholder="Rechercher par nom ou email..."
                  filters={[
                    {
                      key: 'category',
                      label: 'Catégorie',
                      options: [
                        { value: 'all', label: 'Toutes les catégories' },
                        { value: 'individual', label: 'Particulier' },
                        { value: 'professional', label: 'Professionnel' },
                        { value: 'subcontractor', label: 'Sous-traitant' }
                      ]
                    },
                    {
                      key: 'sortBy',
                      label: 'Trier par',
                      options: [
                        { value: 'created_at', label: 'Date de création' },
                        { value: 'name', label: 'Nom' },
                        { value: 'email', label: 'Email' },
                        { value: 'category', label: 'Catégorie' }
                      ]
                    }
                  ]}
                />
              </div>

              <ClientTable
                clients={filteredClients}
                onViewClient={handleViewClient}
                onViewProfile={handleViewClient}
                onGenerateQuote={() => {}}
                onViewHistory={() => {}}
                onUpdateCategory={() => {}}
                onSort={() => {}}
                sortConfig={{}}
                loading={false}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <AddClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddClient}
        onSave={handleAddClient}
      />

      <ClientProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        onEdit={handleEditClient}
        onDelete={handleDeleteClient}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        data={filteredClients}
        filename="clients"
        onExport={() => {}}
      />
    </div>
  );
};

export default ClientDatabase;